import { createContext, useContext, useMemo, useState, ReactNode } from "react";
import { ThemeProvider, CssBaseline, Theme } from "@mui/material";
import { createTheme } from "@mui/material/styles";
import lightTheme from "./themeLight";
import darkTheme from "./themeDark";

type ThemeMode = "light" | "dark";

interface ThemeContextType {
  mode: ThemeMode;
  toggleTheme: () => void;
  setMode: (m: ThemeMode) => void;
}

export const ThemeModeContext = createContext<ThemeContextType | null>(null);

export default function ThemeConfig({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<ThemeMode>(
    (localStorage.getItem("theme-mode") as ThemeMode) || "light"
  );

  const toggleTheme = () => setMode(prev => (prev === "light" ? "dark" : "light"));

  const theme: Theme = useMemo(() => {
    const base = mode === "light" ? lightTheme : darkTheme;

    // Save preference
    localStorage.setItem("theme-mode", mode);

    return createTheme(base, {
      components: {
        /** 🌙 Smooth animated transitions global */
        MuiCssBaseline: {
          styleOverrides: {
            body: {
              transition: "background .4s ease, color .3s ease",
              fontSmoothing: "antialiased",
            },
            "*": {
              transition: "background-color .3s ease, color .3s ease, border-color .3s",
            },
            '::selection': {
              background: base.palette.primary.main,
              color: "#fff",
            },
          },
        },
      },
    });
  }, [mode]);

  return (
    <ThemeModeContext.Provider value={{ mode, setMode, toggleTheme }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}

export const useThemeMode = () => useContext(ThemeModeContext)!;
